"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getChatIdFromMessage = getChatIdFromMessage;
exports.searchUsersByName = searchUsersByName;
exports.searchUsers = searchUsers;
exports.sortRecipientsByNameAlphabetically = sortRecipientsByNameAlphabetically;
exports.searchGroupsByName = searchGroupsByName;
exports.addChatByUpdatedAt = addChatByUpdatedAt;
exports.getAgeFromDob = getAgeFromDob;
const models_1 = require("../models");
const dayjs_1 = __importDefault(require("dayjs"));
/**
 * Resolves the chat ID for a message based on chat type.
 * For group/broadcast: chatId = `to` (the group ID)
 * For private: chatId = the other user's ID (sender for received, recipient for sent)
 */
function getChatIdFromMessage(chatType, to, senderId, currentUserId) {
    if (chatType === models_1.CHAT_TYPE.PRIVATE && senderId !== currentUserId) {
        return senderId;
    }
    return to;
}
function searchUsersByName(str, users) {
    const searchText = str.toLowerCase();
    const filteredUsers = users.filter((user) => {
        const userName = user.name.toLowerCase();
        return userName.includes(searchText);
    });
    return sortByNameStartsWith(filteredUsers, searchText);
}
function searchUsers(str, users) {
    const searchText = str.toLowerCase();
    // An "@" means the user is typing an email — match email only. Without it,
    // matching the email (incl. its shared domain) would surface everyone on the
    // same domain for a short query, so restrict to name + phone instead.
    const isEmailQuery = searchText.includes("@");
    // phoneNo is stored as bare digits (country code lives in a separate field),
    // so strip formatting from the query before matching, e.g. "798-474" → "798474".
    const searchDigits = searchText.replace(/\D/g, "");
    const filteredUsers = users.filter((user) => {
        var _a, _b, _c;
        if (isEmailQuery) {
            return ((_b = (_a = user.email) === null || _a === void 0 ? void 0 : _a.toLowerCase()) !== null && _b !== void 0 ? _b : "").includes(searchText);
        }
        const userName = user.name.toLowerCase();
        const userPhone = (_c = user.phoneNo) !== null && _c !== void 0 ? _c : "";
        return (userName.includes(searchText) ||
            (searchDigits.length > 0 && userPhone.includes(searchDigits)));
    });
    return sortByNameStartsWith(filteredUsers, searchText);
}
function sortByNameStartsWith(users, searchText) {
    return [...users].sort((firstUser, secondUser) => {
        const firstName = firstUser.name.toLowerCase();
        const secondName = secondUser.name.toLowerCase();
        if (firstName.startsWith(searchText) &&
            !secondName.startsWith(searchText)) {
            return -1;
        }
        else if (!firstName.startsWith(searchText) &&
            secondName.startsWith(searchText)) {
            return 1;
        }
        else {
            return 0;
        }
    });
}
function sortRecipientsByNameAlphabetically(recipients, userDirectory) {
    return [...recipients].sort((a, b) => {
        const userA = userDirectory.getUserInfo(a.id);
        const userB = userDirectory.getUserInfo(b.id);
        return userA.name.localeCompare(userB.name);
    });
}
function searchGroupsByName(str, chats) {
    const searchText = str.toLowerCase();
    const broadcastChatData = chats;
    return broadcastChatData.filter((chatData) => {
        const name = chatData.broadcastInfo.name.toLowerCase();
        return name.includes(searchText);
    });
}
function addChatByUpdatedAt(chat, chatList) {
    // Cloning the chatlist to avoid mutating the original object directly for FlashList - https://github.com/Shopify/flash-list/issues/925
    const chatsClone = [...chatList.chats];
    const lastChat = chatsClone[chatsClone.length - 1];
    if (!lastChat) {
        /* Empty chat list */
        chatList.chats = [chat];
        if (chat.unreadCount > 0) {
            chatList.unreadChatCount += 1;
        }
    }
    else if (chat.updatedAt > lastChat.updatedAt) {
        /* Chat belongs in the existing chat list */
        let insertIndex = chatsClone.findIndex(chatObj => chatObj.updatedAt < chat.updatedAt);
        // If no object is found in the array that has an updatedAt value less than the new chat, insert it at the beginning
        if (insertIndex === -1) {
            insertIndex = 0;
        }
        // Insert the new chat at the determined index
        chatsClone.splice(insertIndex, 0, chat);
        chatList.chats = [...chatsClone];
        if (chat.unreadCount > 0) {
            chatList.unreadChatCount += 1;
        }
    }
    else if (!chatList.hasNext) {
        /* Chat list doesn't have any more items which will be loaded on scroll */
        chatList.chats = [...chatsClone, chat];
        if (chat.unreadCount > 0) {
            chatList.unreadChatCount += 1;
        }
    }
    return chatList;
}
function getAgeFromDob(dob) {
    // Parse the app's canonical MM/DD/YYYY format explicitly — Hermes (mobile)
    // can't parse non-ISO date strings via the native Date fallback. Fall back
    // to default parsing for any other (e.g. ISO) format.
    const parsedDob = (0, dayjs_1.default)(dob, "MM/DD/YYYY", true);
    const dobDate = parsedDob.isValid() ? parsedDob : (0, dayjs_1.default)(dob);
    const now = (0, dayjs_1.default)();
    return now.diff(dobDate, "year");
}
